import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Privacy-first form tracking.
 *
 * - NEVER records field values, names, emails or phone numbers.
 * - Only records: an anonymous per-tab session id, which form, the stage
 *   reached, how many fields were filled, and the page path.
 * - Uses sessionStorage (no cookies, cleared when the tab closes).
 * - Fully respects Do Not Track / Global Privacy Control signals.
 */

const SESSION_KEY = "sgre_form_session";

type EventType = "started" | "progress" | "abandoned" | "completed";

const privacySignalActive = () => {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & {
    doNotTrack?: string;
    globalPrivacyControl?: boolean;
    msDoNotTrack?: string;
  };
  const win = typeof window !== "undefined" ? (window as unknown as { doNotTrack?: string }) : undefined;
  return (
    nav.globalPrivacyControl === true ||
    nav.doNotTrack === "1" ||
    nav.msDoNotTrack === "1" ||
    win?.doNotTrack === "1"
  );
};

const getSessionId = (): string | null => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

export interface FormTrackingOptions {
  /** Stable identifier for the form, e.g. "contact", "lead-popup". */
  formId: string;
  /** Number of fields the visitor must complete. */
  totalFields?: number;
}

export function useFormTracking({ formId, totalFields }: FormTrackingOptions) {
  const started = useRef(false);
  const completed = useRef(false);
  const lastStep = useRef<string | null>(null);
  const fieldsFilled = useRef(0);
  const enabled = useRef(false);

  useEffect(() => {
    enabled.current = !privacySignalActive();
  }, []);

  const send = useCallback(
    (eventType: EventType, keepalive = false) => {
      if (!enabled.current) return;
      const sessionId = getSessionId();
      if (!sessionId) return;

      const payload = {
        session_id: sessionId,
        form_id: formId,
        event_type: eventType,
        step: lastStep.current,
        fields_filled: fieldsFilled.current,
        total_fields: totalFields ?? null,
        page_path: typeof window !== "undefined" ? window.location.pathname : null,
      };

      if (keepalive) {
        // Survives the page unloading: keepalive fetch straight to the REST endpoint.
        const url = import.meta.env.VITE_SUPABASE_URL;
        const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        if (url && key) {
          try {
            void fetch(`${url}/rest/v1/form_events`, {
              method: "POST",
              keepalive: true,
              headers: {
                "Content-Type": "application/json",
                apikey: key,
                Authorization: `Bearer ${key}`,
                Prefer: "return=minimal",
              },
              body: JSON.stringify(payload),
            }).catch(() => undefined);
            return;
          } catch {
            /* fall through */
          }
        }
      }

      void supabase
        .from("form_events")
        .insert([payload])
        .then(undefined, () => undefined);
    },
    [formId, totalFields]
  );

  /** Call on first interaction with any field. */
  const trackStart = useCallback(() => {
    if (started.current || completed.current) return;
    started.current = true;
    send("started");
  }, [send]);

  /** Call when a field is filled / a step is reached. */
  const trackStep = useCallback(
    (step: string, filledCount?: number) => {
      trackStart();
      if (completed.current) return;
      lastStep.current = step;
      if (typeof filledCount === "number") fieldsFilled.current = filledCount;
      else fieldsFilled.current = Math.max(fieldsFilled.current, fieldsFilled.current + 1);
    },
    [trackStart]
  );

  /** Call after a successful submission. */
  const trackComplete = useCallback(() => {
    if (completed.current) return;
    completed.current = true;
    lastStep.current = "submitted";
    send("completed");
  }, [send]);

  /** Call when a dialog closes or the visitor leaves mid-form. */
  const trackAbandon = useCallback(
    (keepalive = false) => {
      if (!started.current || completed.current) return;
      started.current = false;
      send("abandoned", keepalive);
    },
    [send]
  );

  // Abandonment on tab close / navigation away / unmount.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") trackAbandon(true);
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", () => trackAbandon(true));
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      trackAbandon(true);
    };
  }, [trackAbandon]);

  return { trackStart, trackStep, trackComplete, trackAbandon };
}
